import React, { useEffect, useRef } from 'react';

/**
 * Mouse-reactive ambient background — mint and teal blobs that drift
 * around the viewport and gently follow the cursor.
 *
 * Structure copied from Valmate / Doorsteps' FluidBackground and
 * recoloured to the PropertyCuts marketing palette (ink, mint, teal).
 * Mounts a fixed full-viewport canvas at z-0; page content is expected
 * to sit at z-10 above it.
 */

interface Blob {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  radius: number;
  baseHue: number;
  hue: number;
  saturation: number;
  lightness: number;
  alpha: number;
  speedX: number;
  speedY: number;
  phase: number;
  phaseSpeed: number;
}

const BLOB_COUNT = 5;

const FluidBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5, active: false });
  const blobsRef = useRef<Blob[]>([]);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const onMove = (e: MouseEvent) => {
      mouseRef.current.x = e.clientX / window.innerWidth;
      mouseRef.current.y = e.clientY / window.innerHeight;
      mouseRef.current.active = true;
    };
    const onLeave = () => {
      mouseRef.current.active = false;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseleave', onLeave);

    // Marketing palette — mint #94e5cf and teal #007d72 on ink #090b0b.
    const palette = [
      { hue: 164, sat: 46, light: 48, alpha: 0.20 },
      { hue: 170, sat: 40, light: 36, alpha: 0.16 },
      { hue: 158, sat: 34, light: 32, alpha: 0.14 },
      { hue: 174, sat: 42, light: 28, alpha: 0.12 },
      { hue: 150, sat: 28, light: 34, alpha: 0.10 },
    ];

    const blobs: Blob[] = [];
    for (let i = 0; i < BLOB_COUNT; i++) {
      const p = palette[i % palette.length];
      blobs.push({
        x: Math.random(),
        y: Math.random(),
        targetX: Math.random(),
        targetY: Math.random(),
        radius: 0.25 + Math.random() * 0.2,
        baseHue: p.hue,
        hue: p.hue,
        saturation: p.sat,
        lightness: p.light,
        alpha: p.alpha,
        speedX: (Math.random() - 0.5) * 0.0003,
        speedY: (Math.random() - 0.5) * 0.0003,
        phase: Math.random() * Math.PI * 2,
        phaseSpeed: 0.003 + Math.random() * 0.004,
      });
    }
    blobsRef.current = blobs;

    const draw = () => {
      const w = canvas.width;
      const h = canvas.height;
      const mouse = mouseRef.current;

      ctx.fillStyle = '#090b0b';
      ctx.fillRect(0, 0, w, h);

      for (const blob of blobs) {
        blob.phase += blob.phaseSpeed;
        blob.targetX += blob.speedX + Math.sin(blob.phase) * 0.0002;
        blob.targetY += blob.speedY + Math.cos(blob.phase * 0.7) * 0.0002;

        if (blob.targetX < -0.1 || blob.targetX > 1.1) blob.speedX *= -1;
        if (blob.targetY < -0.1 || blob.targetY > 1.1) blob.speedY *= -1;
        blob.targetX = Math.max(-0.2, Math.min(1.2, blob.targetX));
        blob.targetY = Math.max(-0.2, Math.min(1.2, blob.targetY));

        if (mouse.active) {
          const dx = mouse.x - blob.targetX;
          const dy = mouse.y - blob.targetY;
          blob.targetX += dx * 0.008;
          blob.targetY += dy * 0.008;
          const hueShift = (mouse.x - 0.5) * 12 + (mouse.y - 0.5) * 8;
          blob.hue = blob.baseHue + hueShift;
        } else {
          blob.hue += (blob.baseHue - blob.hue) * 0.01;
        }

        blob.x += (blob.targetX - blob.x) * 0.02;
        blob.y += (blob.targetY - blob.y) * 0.02;

        const cx = blob.x * w;
        const cy = blob.y * h;
        const r = blob.radius * Math.max(w, h);

        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        const h1 = ((blob.hue % 360) + 360) % 360;
        grad.addColorStop(
          0,
          `hsla(${h1}, ${blob.saturation}%, ${blob.lightness}%, ${blob.alpha})`,
        );
        grad.addColorStop(
          0.5,
          `hsla(${h1}, ${blob.saturation}%, ${blob.lightness * 0.7}%, ${blob.alpha * 0.4})`,
        );
        grad.addColorStop(
          1,
          `hsla(${h1}, ${blob.saturation}%, ${blob.lightness * 0.5}%, 0)`,
        );

        ctx.globalCompositeOperation = 'lighter';
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);
      }

      ctx.globalCompositeOperation = 'source-over';
      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-0"
      style={{ width: '100vw', height: '100vh' }}
      aria-hidden="true"
    />
  );
};

export default FluidBackground;
